/**
 * RunningComparisonView — shared UI for RunningComparison and SharedRunningComparison.
 *
 * Manages tab state, hover/zoom synchronisation, scrubber time, and selected
 * climb/descent.  The caller is responsible for data fetching, the swap
 * transformation on `resampled`, and any dialogs.
 *
 * Props
 * -----
 * analysisA, analysisB  — workout analysis objects (already swapped if needed)
 * alignment             — DTW alignment result
 * resampled             — already-transformed resampled grid (swap applied by caller)
 * checkpoints           — checkpoint comparison array
 * headerSection         — JSX rendered above the tabs Paper
 *                         (back/share row + ComparisonHeader + alerts)
 * extraDialogs          — JSX for any dialogs the caller owns (share, unshare, …)
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Paper,
} from '@mui/material';
import { tokens } from '../../theme';

import GapChart from './comparison/GapChart';
import ComparisonMetricCharts from './comparison/ComparisonMetricCharts';
import GhostRaceMap from './comparison/GhostRaceMap';
import GhostScrubber from './comparison/GhostScrubber';
import ComparisonClimbsGrid from './comparison/ComparisonClimbsGrid';
import ComparisonDescentsGrid from './comparison/ComparisonDescentsGrid';
import ClimbProfileChart from './comparison/ClimbProfileChart';
import DescentProfileChart from './comparison/DescentProfileChart';
import ComparisonSummary from './comparison/ComparisonSummary';
import CheckpointComparisonTable from './comparison/CheckpointComparisonTable';

// ---------------------------------------------------------------------------

export default function RunningComparisonView({
  analysisA,
  analysisB,
  alignment,
  resampled,
  checkpoints,
  headerSection,
  extraDialogs,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [hoverDistanceM, setHoverDistanceM] = useState(null);
  const [zoomDomain, setZoomDomain] = useState(null);
  const [scrubberTime, setScrubberTime] = useState(0);
  const [selectedClimb, setSelectedClimb] = useState(null);
  const [selectedDescent, setSelectedDescent] = useState(null);

  return (
    <Box m={isMobile ? '2px' : '20px'}>
      {headerSection}

      {/* ── Tabs ──────────────────────────────────────────────────────── */}
      <Paper
        sx={{
          mt: 2,
          backgroundColor: colors.grey[800],
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            backgroundColor: colors.grey[900],
            borderBottom: `1px solid ${colors.grey[700]}`,
            '& .MuiTab-root': { color: colors.grey[300] },
            '& .Mui-selected': { color: colors.blueAccent[300] },
            '& .MuiTabs-indicator': { backgroundColor: colors.blueAccent[400] },
          }}
        >
          <Tab label="Race Dynamics" />
          <Tab label="Map & Ghost Race" />
          <Tab label="Climbs" />
          <Tab label="Descents" />
          <Tab label="Summary" />
        </Tabs>

        <Box p={isMobile ? 0.5 : 2}>

          {/* ── Tab 0: Race Dynamics ── */}
          {tab === 0 && (
            <Box>
              <GapChart
                resampled={resampled}
                checkpoints={checkpoints}
                hoverDistanceM={hoverDistanceM}
                onHover={setHoverDistanceM}
                zoomDomain={zoomDomain}
                onZoomChange={setZoomDomain}
                onCheckpointClick={() => setTab(4)}
                onOpenGhostRace={(timeS) => { setScrubberTime(timeS); setTab(1); }}
              />
              <Box mt={3}>
                <ComparisonMetricCharts
                  resampled={resampled}
                  hoverDistanceM={hoverDistanceM}
                  onHover={setHoverDistanceM}
                  zoomDomain={zoomDomain}
                  onZoomChange={setZoomDomain}
                />
              </Box>
              {checkpoints?.length > 0 && (
                <Box mt={4}>
                  <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1}>
                    Checkpoint Timeline
                  </Typography>
                  <CheckpointComparisonTable checkpoints={checkpoints} race={analysisA?.race} />
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 1: Map & Ghost Race ── */}
          {tab === 1 && (
            <Box>
              <GhostRaceMap
                analysisA={analysisA}
                analysisB={analysisB}
                resampled={resampled}
                scrubberTime={scrubberTime}
                checkpoints={checkpoints}
              />
              <Box mt={2}>
                <GhostScrubber
                  resampled={resampled}
                  scrubberTime={scrubberTime}
                  onTimeChange={setScrubberTime}
                  checkpoints={checkpoints}
                />
              </Box>
            </Box>
          )}

          {/* ── Tab 2: Climbs ── */}
          {tab === 2 && (
            <Box>
              <ClimbProfileChart
                resampled={resampled}
                commonClimbs={alignment?.common_climbs || null}
                climbsA={analysisA?.climbs || []}
                selectedClimb={selectedClimb}
                onClimbClick={setSelectedClimb}
                checkpoints={checkpoints}
              />
              <Box mt={3}>
                <ComparisonClimbsGrid
                  climbsA={analysisA?.climbs || []}
                  climbsB={analysisB?.climbs || []}
                  commonClimbs={alignment?.common_climbs || null}
                  selectedClimb={selectedClimb}
                  onClimbSelect={setSelectedClimb}
                  onClimbFocus={(start, end) => { setZoomDomain([start, end]); setTab(0); }}
                />
              </Box>
            </Box>
          )}

          {/* ── Tab 3: Descents ── */}
          {tab === 3 && (
            <Box>
              <DescentProfileChart
                resampled={resampled}
                commonDescents={alignment?.common_descents || null}
                descentsA={analysisA?.descents || []}
                selectedDescent={selectedDescent}
                onDescentClick={setSelectedDescent}
                checkpoints={checkpoints}
              />
              <Box mt={3}>
                <ComparisonDescentsGrid
                  descentsA={analysisA?.descents || []}
                  descentsB={analysisB?.descents || []}
                  commonDescents={alignment?.common_descents || null}
                  selectedDescent={selectedDescent}
                  onDescentSelect={setSelectedDescent}
                  onDescentFocus={(start, end) => { setZoomDomain([start, end]); setTab(0); }}
                />
              </Box>
            </Box>
          )}

          {/* ── Tab 4: Summary ── */}
          {tab === 4 && (
            <ComparisonSummary
              analysisA={analysisA}
              analysisB={analysisB}
              checkpoints={checkpoints}
            />
          )}

        </Box>
      </Paper>

      {extraDialogs}
    </Box>
  );
}
